"use client";

import React, { useEffect, useCallback, useRef } from "react";
import { useGoogleDriveCache } from "@/lib/contexts/GoogleDriveCacheContext";
import { useUniversalAuth } from "@wtc/ui";
import { PreloadStatus } from "@/lib/services/google-drive-preloader";

export interface UseGoogleDrivePreloaderOptions {
  enabled?: boolean;
  delayMs?: number; // Delay before starting preload to not block initial app load
  preloadOnMount?: boolean;
  preloadOnAuth?: boolean;
}

/**
 * Hook to manage Google Drive preloading
 * Automatically starts preloading when user is authenticated
 */
export function useGoogleDrivePreloader(options: UseGoogleDrivePreloaderOptions = {}) {
  const {
    enabled = true,
    delayMs = 2000, // 2 second delay to let app finish loading
    preloadOnMount = true,
    preloadOnAuth = true,
  } = options;

  const { session } = useUniversalAuth();
  const cache = useGoogleDriveCache();
  const hasStartedRef = useRef(false);

  // Manual preload trigger
  const startPreload = useCallback(async () => {
    if (!enabled || !session?.access_token) {
      console.log("🚀 Google Drive Preloader: Skipping preload - not enabled or no token");
      return;
    }

    console.log("🚀 Google Drive Preloader: Starting manual preload...");
    await cache.startPreload(session.access_token);
  }, [enabled, session?.access_token, cache]);

  // Delayed preload trigger
  const startDelayedPreload = useCallback(async () => {
    if (!enabled || !session?.access_token || hasStartedRef.current) {
      return;
    }

    console.log(`🚀 Google Drive Preloader: Scheduling preload in ${delayMs}ms...`);
    hasStartedRef.current = true;

    setTimeout(async () => {
      try {
        await cache.startPreload(session.access_token);
      } catch (error) {
        console.error("🚀 Google Drive Preloader: Delayed preload failed:", error);
        hasStartedRef.current = false; // Reset on error to allow retry
      }
    }, delayMs);
  }, [enabled, session?.access_token, cache, delayMs]);

  // Auto-preload on mount
  useEffect(() => {
    if (preloadOnMount && session?.access_token && !hasStartedRef.current) {
      void startDelayedPreload();
    }
  }, [preloadOnMount, session?.access_token, startDelayedPreload]);

  // Auto-preload when authentication changes
  useEffect(() => {
    if (preloadOnAuth && session?.access_token && !hasStartedRef.current) {
      // Small delay to let auth settle
      setTimeout(() => {
        void startDelayedPreload();
      }, 500);
    }
  }, [preloadOnAuth, session?.access_token, startDelayedPreload]);

  // Stop preload function
  const stopPreload = useCallback(() => {
    cache.stopPreload();
  }, [cache]);

  // Get preload status
  const getPreloadStatus = useCallback((): PreloadStatus => {
    return cache.getPreloadStatus();
  }, [cache]);

  return {
    startPreload,
    startDelayedPreload,
    stopPreload,
    getPreloadStatus,
    isAuthenticated: !!session?.access_token,
    isEnabled: enabled,
  };
}

/**
 * Hook for monitoring preload status with real-time updates
 */
export function useGoogleDrivePreloadStatus(refreshInterval: number = 1000) {
  const cache = useGoogleDriveCache();
  const [status, setStatus] = React.useState<PreloadStatus>(cache.getPreloadStatus());

  useEffect(() => {
    if (!refreshInterval) return;

    const interval = setInterval(() => {
      const currentStatus = cache.getPreloadStatus();
      setStatus(currentStatus);
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [cache, refreshInterval]);

  return status;
}

/**
 * Simple hook that just ensures preloading happens on app load
 * Use this in your root layout or main app component
 */
export function useAutoPreload(delayMs: number = 3000, enabled: boolean = true) {
  const { session } = useUniversalAuth();
  const cache = useGoogleDriveCache();
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (enabled && session?.access_token && !hasStartedRef.current) {
      console.log("🚀 Google Drive Auto-Preloader: User authenticated, starting preload...");
      hasStartedRef.current = true;

      setTimeout(async () => {
        try {
          await cache.startPreload(session.access_token);
        } catch (error) {
          console.error("🚀 Google Drive Auto-Preloader: Preload failed:", error);
          hasStartedRef.current = false; // Reset on error to allow retry
        }
      }, delayMs);
    }
  }, [enabled, session?.access_token, cache, delayMs]);

  return { isAuthenticated: !!session?.access_token };
}
